import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  BarraDeLista, BuscaDaBarra, FiltrosAtivos, RodapeDaLista, usePaginacao, type FiltroAtivo,
} from '../../BarraDeLista';
import {
  codigoDaCampanha, criarCampanha, listarCampanhas, type CampanhaDaLista,
} from '../../api/cptu';
import { Seletor, SeletorDeCliente, SeletorDeOs, type OpcaoDeFiltro } from '../../Filtros';
import { dataDoDia, dataHora, dataIso, numero, plural } from '../../formato';
import { Periodo } from '../../Periodo';
import { useSessao } from '../../sessao';
import { degrauDe, SITUACAO_DA_CAMPANHA_CPTU } from '../../situacoes';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../../TabelaComFicha';
import {
  AcaoBloqueada, Campo, Carregando, ColunaOrdenavel, Dialogo, ErroDaApi, ordenarPor,
  useEstreito, useOrdenacao, Vazio,
} from '../../ui';
import { PERMISSAO_DE_EDITAR_CPTU, SeloDaCampanha } from './CampanhaMoldura';
import { FormularioDaJanela } from './FormularioDaJanela';

/**
 * A lista das campanhas CPTu — onda 312, Tarefa 312.5 (spec 07 §4; RN-253).
 *
 * O padrão de lista Carbon Testesolo (P-LST): o cabeçalho com a frase da
 * ordem e UMA ação primária («＋ Nova campanha», no computador; sem
 * `cptu.editar`, à vista e desativada com o motivo — P-UI-24), a barra
 * colada à tabela (busca, Período da criação, situação, cliente), os filtros
 * ativos removíveis com «N de M», as colunas que ordenam, a tabela com ficha
 * e o rodapé. Os filtros moram no ENDEREÇO (P-LST-05): o F5 e o link
 * mandado a um colega abrem o mesmo recorte. No celular a lista é de cartões
 * — cada cartão um alvo só, que abre a campanha (P-LST-14).
 *
 * A busca, a situação e o cliente vão à API; o Período e a ordem são feitos
 * aqui, na lista que já chegou (dezenas de campanhas, não milhares).
 *
 * A campanha nasce de uma OS (a janela escolhe a OS e o título) e abre na
 * casca, pronta para a importação.
 */

const SITUACOES: OpcaoDeFiltro[] = Object.entries(SITUACAO_DA_CAMPANHA_CPTU)
  .map(([valor, s]) => ({ valor, texto: s.texto }));

/** A ordem da escada (rascunho primeiro): a seta de «Situação» segue o caminho da campanha. */
const ORDEM_DA_SITUACAO = Object.keys(SITUACAO_DA_CAMPANHA_CPTU);

const CHAVES_DE_ORDEM: { [campo: string]: (c: CampanhaDaLista) => string | number | null } = {
  col_criada: (c) => c.criada_em,
  col_os: (c) => c.os_numero,
  col_cliente: (c) => c.cliente,
  col_verticais: (c) => c.verticais,
  col_metragem: (c) => (c.verticais ? c.metragem : null),
  col_situacao: (c) => {
    const i = ORDEM_DA_SITUACAO.indexOf(c.situacao);
    return i < 0 ? ORDEM_DA_SITUACAO.length : i;
  },
};

const NOME_DA_ORDEM: { [campo: string]: string } = {
  col_criada: 'criada em', col_os: 'OS', col_cliente: 'cliente', col_verticais: 'verticais',
  col_metragem: 'metragem', col_situacao: 'situação',
};

export function Campanhas() {
  const { pode, eu } = useSessao();
  const estreito = useEstreito();
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState(() => params.get('busca') ?? '');
  const [situacao, setSituacao] = useState<string | null>(() => params.get('situacao'));
  const [cliente, setCliente] = useState<string | null>(() => params.get('cliente'));
  const [de, setDe] = useState(() => params.get('de') ?? '');
  const [ate, setAte] = useState(() => params.get('ate') ?? '');
  const [linhas, setLinhas] = useState<CampanhaDaLista[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [nova, setNova] = useState(false);
  const { ordem, sentido, trocar } = useOrdenacao('col_criada', {
    descendentes: ['col_criada', 'col_verticais', 'col_metragem'],
  });

  /* O endereço acompanha os filtros (`replace`: uma tecla da busca não vira um «voltar»). */
  useEffect(() => {
    const q = new URLSearchParams();
    if (busca.trim()) q.set('busca', busca.trim());
    if (situacao) q.set('situacao', situacao);
    if (cliente) q.set('cliente', cliente);
    if (de) q.set('de', de);
    if (ate) q.set('ate', ate);
    setParams(q, { replace: true });
  }, [busca, situacao, cliente, de, ate, setParams]);

  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => {
      listarCampanhas({ busca, situacao, cliente })
        .then((r) => { if (vivo) { setLinhas(r); setErro(null); } })
        .catch((e) => { if (vivo) setErro(e); });
    }, busca ? 300 : 0);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca, situacao, cliente]);

  /* O Período sobre a criação, e a ordem — na lista que chegou. */
  const recortadas = useMemo(() => {
    const noPeriodo = (linhas ?? []).filter((c) => {
      const dia = dataIso(c.criada_em);
      return (!de || dia >= de) && (!ate || dia <= ate);
    });
    return ordenarPor(noPeriodo, CHAVES_DE_ORDEM[ordem] ?? CHAVES_DE_ORDEM.col_criada, sentido);
  }, [linhas, de, ate, ordem, sentido]);

  const pag = usePaginacao(recortadas);
  const ativos: FiltroAtivo[] = [];
  if (situacao) {
    ativos.push({ chave: 'sit', texto: `Situação: ${degrauDe(SITUACAO_DA_CAMPANHA_CPTU, situacao).texto}`,
      tirar: () => setSituacao(null) });
  }
  if (cliente) ativos.push({ chave: 'cli', texto: 'Cliente escolhido', tirar: () => setCliente(null) });
  if (busca.trim()) ativos.push({ chave: 'q', texto: `Busca: ${busca.trim()}`, tirar: () => setBusca('') });
  if (de || ate) {
    ativos.push({
      /* O dia como foi escolhido (`dataDoDia`: o `data()` leria «2026-10-01» como meia-noite UTC, um dia antes). */
      chave: 'per', texto: `Criada: ${de ? dataDoDia(de) : '…'} – ${ate ? dataDoDia(ate) : '…'}`,
      tirar: () => { setDe(''); setAte(''); },
    });
  }
  const criaAqui = pode('cptu.editar') && !estreito;
  const textoDaOrdem = `${NOME_DA_ORDEM[ordem] ?? 'criada em'} ${sentido === 'asc' ? '↑' : '↓'}`;

  return (
    <div className="lista-cptu">
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Campanhas CPTu</h1>
          <p className="sub">As campanhas de piezocone, da importação ao relatório — a mais recente primeiro.</p>
        </div>
        {criaAqui && (
          <button type="button" className="botao" onClick={() => setNova(true)}>＋ Nova campanha</button>
        )}
        {!estreito && !pode('cptu.editar') && (
          <AcaoBloqueada texto="＋ Nova campanha" oQueFaz="não cria campanha CPTu"
            permissao={PERMISSAO_DE_EDITAR_CPTU} persona={eu?.atuandoPor ? eu.nome : null} />
        )}
      </div>

      <BarraDeLista rotulo="Filtros das campanhas">
        <BuscaDaBarra valor={busca} aoMudar={setBusca} placeholder="OS, cliente, obra ou título"
          rotulo="Buscar campanha" />
        <Periodo valor={{ de, ate }} rotulo="Criada"
          aoMudar={(v) => { setDe(v.de); setAte(v.ate); }}
          atalhos={['mes', 'mesPassado', 'ultimos30', 'tudo']} />
        <Seletor naBarra rotulo="Situação" valor={situacao} opcoes={SITUACOES} aoMudar={setSituacao}
          todos="Todas as situações" />
        <SeletorDeCliente naBarra valor={cliente} aoMudar={setCliente} />
      </BarraDeLista>

      <FiltrosAtivos itens={ativos}
        aoLimpar={() => { setSituacao(null); setCliente(null); setBusca(''); setDe(''); setAte(''); }}
        contagem={linhas ? `${recortadas.length} de ${plural(linhas.length, 'campanha', 'campanhas')}` : undefined} />

      <ErroDaApi erro={erro} />
      {linhas === null && !erro && <Carregando texto="Carregando as campanhas" />}

      {linhas && recortadas.length === 0 && (
        <section className="cartao">
          <Vazio>
            {ativos.length > 0
              ? 'Nenhuma campanha com estes filtros. Use «Limpar filtros» para ver todas.'
              : criaAqui
                ? 'Nenhuma campanha CPTu ainda. Comece por «＋ Nova campanha»: ela nasce de uma OS.'
                : 'Nenhuma campanha CPTu ainda.'}
          </Vazio>
        </section>
      )}

      {linhas && recortadas.length > 0 && (estreito ? (
        <ul className="cptu-cartoes" aria-label="Campanhas CPTu">
          {pag.visiveis.map((c) => (
            <li key={c.id}>
              <Link className="cptu-cartao" to={`/cptu/${c.id}`}>
                <span className="cptu-cartao-topo">
                  <b className="mono">OS {c.os_numero} · {codigoDaCampanha(c.sequencia)}</b>
                  <SeloDaCampanha situacao={c.situacao} />
                </span>
                <span className="cptu-cartao-titulo">{c.titulo}</span>
                {c.cliente && <span className="sub">{c.cliente}{c.obra ? ` · ${c.obra}` : ''}</span>}
                <span className="sub">
                  {plural(c.verticais, 'vertical', 'verticais')}
                  {c.verticais > 0 && ` · ${numero(c.metragem, 1)} m`}
                  {c.relatorio_numero && ` · relatório ${c.relatorio_numero}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <section className="cartao">
          <TabelaComFicha
            linhas={pag.visiveis}
            chave={(c) => c.id}
            nome={(c) => `campanha ${codigoDaCampanha(c.sequencia)} da OS ${c.os_numero}`}
            rotulo="Campanhas CPTu"
            colunas={colunasDaLista({ ordem, sentido, trocar })}
            ficha={fichaDaCampanha}
          />
          <RodapeDaLista {...pag} ordem={textoDaOrdem} />
        </section>
      ))}
      {linhas && recortadas.length > 0 && estreito && <RodapeDaLista {...pag} ordem={textoDaOrdem} />}

      {nova && <JanelaDeNovaCampanha aoFechar={() => setNova(false)} />}
    </div>
  );
}

function colunasDaLista({ ordem, sentido, trocar }: {
  ordem: string; sentido: 'asc' | 'desc'; trocar(campo: string): void;
}): ColunaComFicha<CampanhaDaLista>[] {
  const col = (campo: string, texto: string, classe?: string, opcional = false) => (
    <ColunaOrdenavel campo={campo} texto={texto} classe={classe} opcional={opcional}
      ordem={ordem} sentido={sentido} aoTrocar={trocar} />
  );
  return [
    {
      chave: 'os',
      cabeca: col('col_os', 'OS'),
      celula: (c) => (
        <th scope="row">
          <Link to={`/cptu/${c.id}`} className="cod">OS {c.os_numero}</Link>
        </th>
      ),
    },
    {
      chave: 'cliente',
      cabeca: col('col_cliente', 'Cliente'),
      celula: (c) => <td>{c.cliente ?? <span className="nada">sem cliente</span>}</td>,
    },
    {
      chave: 'obra', detalhe: true,
      cabeca: <th scope="col" className="col-opcional">Obra</th>,
      celula: (c) => <td className="col-opcional">{c.obra ?? <span className="nada">—</span>}</td>,
    },
    {
      chave: 'campanha',
      cabeca: <th scope="col">Campanha</th>,
      celula: (c) => (
        <td>
          {c.titulo}
          <span className="sub mono">{codigoDaCampanha(c.sequencia)}</span>
        </td>
      ),
    },
    {
      chave: 'verticais', detalhe: true,
      cabeca: col('col_verticais', 'Verticais', 'n', true),
      celula: (c) => <td className="n mono col-opcional">{c.verticais}</td>,
    },
    {
      chave: 'metragem', detalhe: true,
      cabeca: col('col_metragem', 'Metragem', 'n', true),
      celula: (c) => (
        <td className="n mono col-opcional">{c.verticais ? `${numero(c.metragem, 1)} m` : <span className="nada">—</span>}</td>
      ),
    },
    {
      chave: 'situacao',
      cabeca: col('col_situacao', 'Situação'),
      celula: (c) => <td><SeloDaCampanha situacao={c.situacao} /></td>,
    },
    {
      chave: 'relatorio', detalhe: true,
      cabeca: <th scope="col" className="col-opcional">Relatório</th>,
      celula: (c) => (
        <td className="col-opcional mono">{c.relatorio_numero ?? <span className="nada">—</span>}</td>
      ),
    },
  ];
}

function fichaDaCampanha(c: CampanhaDaLista): FichaDaLinha {
  return {
    rotulo: 'Campanha CPTu',
    titulo: `${codigoDaCampanha(c.sequencia)} · OS ${c.os_numero}`,
    tituloCodigo: true,
    sub: (
      <>
        <span>{c.titulo}</span>
        {c.cliente && <span>{c.cliente}</span>}
        {c.obra && <span>{c.obra}</span>}
      </>
    ),
    corpo: (
      <>
        <div className="tf-selos"><SeloDaCampanha situacao={c.situacao} /></div>
        <CamposDaFicha itens={[
          ['Verticais', c.verticais ? String(c.verticais) : 'nenhuma ainda'],
          ['Metragem', c.verticais ? `${numero(c.metragem, 1)} m` : null],
          ['Relatório', c.relatorio_numero ? <span className="mono">{c.relatorio_numero}</span> : 'ainda não emitido'],
          ['Criada em', dataHora(c.criada_em)],
        ]} />
      </>
    ),
    acoes: <Link className="botao" to={`/cptu/${c.id}`}>Abrir</Link>,
  };
}

/**
 * A campanha nova: a OS e o título. A sequência (-CPTU-01, -02) é a da OS,
 * dada pelo servidor; o autor é quem está na sessão. Abre na casca. O foco
 * abre no primeiro campo, e o Enter no título cria (`FormularioDaJanela`).
 */
export function JanelaDeNovaCampanha({ os, aoFechar }: {
  /** A OS já escolhida (o Campo da OS); sem ela, a janela pede a OS. */
  os?: { id: string; numero: string };
  aoFechar(): void;
}) {
  const navegar = useNavigate();
  const [osId, setOsId] = useState<string | null>(os?.id ?? null);
  const [titulo, setTitulo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const falta = !osId ? 'Escolha a ordem de serviço da campanha.'
    : !titulo.trim() ? 'Dê um título à campanha.' : null;

  const criar = async () => {
    if (!osId || falta || ocupado) return;
    setOcupado(true); setErro(null);
    try {
      const r = await criarCampanha(osId, titulo.trim());
      navegar(`/cptu/${r.campanha.id}`);
    } catch (e) { setErro(e); setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Nova campanha CPTu" rotulo={os ? `OS ${os.numero}` : undefined} aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void criar(); }}>
        <ErroDaApi erro={erro} />
        {!os && <SeletorDeOs valor={osId} cliente={null} aoMudar={setOsId} />}
        <Campo rotulo="Título da campanha" value={titulo} maxLength={200} required enterKeyHint="done"
          dica="Como a campanha aparece na lista e no relatório — ex.: «Galpão logístico — 10 verticais»."
          onChange={(e) => setTitulo(e.target.value)} />
        <p className="sub">
          A campanha nasce em rascunho, com a sequência seguinte da OS (CPTU-01, CPTU-02…). Depois, importe os
          arquivos do equipamento.
        </p>
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="submit" className="botao" aria-disabled={!!falta || ocupado}>
            {ocupado ? 'Criando…' : 'Criar a campanha'}
          </button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}
